import React, { memo } from "react";
import {
  Box,
  Accordion as ChakraAccordion,
  AccordionItem,
  AccordionButton,
  Collapse,
  AccordionIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { useFormikContext } from "formik";

/**
 * AccordionSectionProps
 * @typedef {Object} AccordionSectionProps
 * @prop {JSX.Element} children
 * @prop {String} title
 * @prop {String} section
 * @prop {Boolean} isDisabled
 * @prop {Boolean} hasErrors
 * @prop {Boolean} hasBeenTouched
 */

/**
 * AccordionSection
 * @param {AccordionSectionProps} props
 * @description
 * A Formik context aware accordion section that knows when to be disabled and if
 * it has been completed (i.e. is valid) or has an error within it (i.e. is invalid).
 */
export const AccordionSection = memo(
  ({ children, title, isDisabled, hasErrors, hasBeenTouched }) => {
    const errorColor = useColorModeValue("red.500", "red.300");
    const validColor = useColorModeValue("green.500", "green.400");

    return (
      <AccordionItem isDisabled={isDisabled}>
        {({ isExpanded }) => {
          const isInvalidInForm = hasErrors && hasBeenTouched; // is this section invalid in Formik's schema
          const isVisuallyInvalid = !isExpanded && !isDisabled && isInvalidInForm; // should this section display the invalid header
          const isVisuallyValid = !isExpanded && !isDisabled && !hasErrors && hasBeenTouched; // should this section display the valid header

          let icon = <AccordionIcon data-testid="default-icon" />;
          if (isVisuallyInvalid) {
            icon = <WarningIcon color={errorColor} w={6} h={6} data-testid="invalid-icon" />;
          } else if (isVisuallyValid) {
            icon = <CheckCircleIcon color={validColor} w={6} h={6} data-testid="valid-icon" />;
          }

          return (
            <>
              <AccordionButton
                py={3}
                _expanded={{
                  bg: "integrator.500",
                  color: "white",
                  cursor: "default",
                  borderTopColor: "integrator.500",
                }}
              >
                <Box flex="1" textAlign="left" color={isVisuallyInvalid ? errorColor : undefined}>
                  {title}
                </Box>

                {icon}
              </AccordionButton>
              <Collapse in={isExpanded} unmountOnExit>
                <Box p={4}>{children}</Box>
              </Collapse>
            </>
          );
        }}
      </AccordionItem>
    );
  }
);

/**
 * Props used by the <Accordion /> component
 * @typedef {Object} AccordionProps
 * @prop {Number} activeSection
 * @prop {Function} setActiveSection
 * @prop {AccordionSection[]} children
 */

/**
 * Accordion
 * @param {AccordionProps} props
 * @todo Might be worth having this component capable of being uncontrolled?
 */
export default function Accordion({ activeSection, setActiveSection, children }) {
  const { errors, touched } = useFormikContext();

  return (
    <ChakraAccordion index={activeSection} onChange={setActiveSection}>
      {React.Children.map(children, (child) => {
        if (!child) return null;

        const { previousSection, section } = child.props;
        const isDisabled =
          previousSection && (errors[previousSection] || !touched[previousSection]);
        const hasErrors = errors[section];
        const hasBeenTouched = touched[section];

        return React.cloneElement(child, { isDisabled, hasErrors, hasBeenTouched });
      })}
    </ChakraAccordion>
  );
}
